import React, { ChangeEvent, useState, useEffect } from 'react'
import { Controller, useFormContext } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { isEmpty } from 'lodash'

import {
  Dropdown,
  FormControl,
  InputGroup,
  TextInput,
  TextareaInput,
  Loader,
} from 'components'
import {
  findSelected,
  getPriceFormatter,
  validateKeyPress,
  trimFirstSpace,
  handleDisableOnPaste,
} from 'helpers/common'
import { MAINTENANCE_UNIT_OPTIONS, LIMIT } from 'helpers/constants'

import { OptionProps, CURRENCY, MAINTENANCE_UNIT } from 'types'
import type { IStockKeepingUnitForm } from 'features/StockKeepingUnit'

function StockKeepingUnitDetail(): JSX.Element {
  const [maintenancePeriodChecking, setMaintenancePeriodChecking] =
    useState<boolean>(false)
  const { control, errors, setValue, watch, trigger, clearErrors } =
    useFormContext<IStockKeepingUnitForm>()

  const { t } = useTranslation('sku')

  const base = 'f-stock-keeping-unit__form'
  const maxLengthPrice = LIMIT.PRICE
  const maintenancePeriod = watch('maintenancePeriod')
  const maintenanceUnit = watch('maintenanceUnit')?.key

  let errorMessageMaintenance = undefined

  if (Boolean(errors.maintenanceUnit?.message)) {
    errorMessageMaintenance = errors.maintenanceUnit?.message
  }

  if (Boolean(errors.maintenancePeriod?.message)) {
    errorMessageMaintenance = errors.maintenancePeriod?.message
  }

  useEffect(() => {
    if (maintenancePeriod && maintenanceUnit) {
      clearErrors(['maintenancePeriod', 'maintenanceUnit'])
    }
  }, [maintenancePeriod, maintenanceUnit])

  const currencyOption = Object.entries(CURRENCY).map(([key, label]) => ({
    key,
    label,
  }))

  const handleChangeValue = (
    e: ChangeEvent<HTMLInputElement>,
    key: string,
    maxLength: number,
  ) => {
    const value = e.target.value
    if (value.charAt(0) === '0') {
      return
    }
    setValue(key, value.slice(0, maxLength), { shouldDirty: true })
  }

  const handleChangePriceValue = (
    e: ChangeEvent<HTMLInputElement>,
    key: string,
    maxLength: number,
  ) => {
    let value = e.target.value

    if (value.indexOf('.') >= 0) {
      const decimalPosition = value.indexOf('.')

      // split number by decimal point
      let leftSide = value.substring(0, decimalPosition)
      let rightSide = value.substring(decimalPosition)

      leftSide = getPriceFormatter(leftSide)
      rightSide = getPriceFormatter(rightSide)
      rightSide = rightSide.substring(0, 2)

      // join number by .
      value = leftSide + '.' + rightSide
    } else {
      value = value.length <= 10 ? getPriceFormatter(value) : value
      value = value.slice(0, maxLength - 3)
    }

    let newValue = value

    if (
      newValue.charAt(0) === '0' &&
      newValue.charAt(1) !== '0' &&
      newValue.charAt(1) !== '.'
    ) {
      newValue = getPriceFormatter(`${parseFloat(newValue.replace(/\D/g, ''))}`)
    }

    if (newValue.slice(0, 2) === '00') {
      newValue = '0'
    }

    setValue(key, newValue, { shouldDirty: true })
  }

  const handleBlurPriceValue = (
    e: ChangeEvent<HTMLInputElement>,
    key: string,
  ) => {
    if (!isEmpty(e.target.value)) {
      return
    }

    setValue(key, '0.00', { shouldDirty: true })
  }

  const handleChangeMaintenanceUnit = (value: OptionProps) => {
    const key = value.key

    if (key === MAINTENANCE_UNIT.NONE) {
      setValue('maintenancePeriod', '', { shouldDirty: true })
    }

    setValue('maintenanceUnit', value, { shouldDirty: true })
  }

  return (
    <>
      <div className={`${base}__description-layout-status`}>
        <FormControl label={t('costPrice.title')}>
          <InputGroup
            className={`${base}__group-currency-input`}
            variants='ternary'
          >
            <Controller
              type='text'
              name='costCurrency'
              control={control}
              render={(field) => (
                <Dropdown
                  className={`${base}__prefix`}
                  group='right'
                  options={currencyOption}
                  selected={field.value}
                  {...field}
                />
              )}
            />

            <Controller
              type='text'
              className={`${base}__group-currency-input--currency-input`}
              name='costPrice'
              control={control}
              render={({ value }) => (
                <TextInput
                  type='text'
                  className={`${base}__suffix`}
                  placeholder={t('insertCostPrice.placeholder')}
                  group='left'
                  onChange={(e) => {
                    handleChangePriceValue(e, 'costPrice', maxLengthPrice)
                  }}
                  onBlur={(e) => handleBlurPriceValue(e, 'costPrice')}
                  value={value}
                  onKeyPress={validateKeyPress(new RegExp(/^\d*\.?\d*$/u))}
                  onPaste={(e) => handleDisableOnPaste(e)}
                  maxLength={maxLengthPrice}
                />
              )}
            />
          </InputGroup>
        </FormControl>

        <FormControl label={t('salePrice.title')}>
          <InputGroup
            className={`${base}__group-currency-input`}
            variants='ternary'
          >
            <Controller
              name='saleCurrency'
              control={control}
              render={(field) => (
                <Dropdown
                  className={`${base}__prefix`}
                  group='right'
                  options={currencyOption}
                  selected={field.value}
                  {...field}
                />
              )}
            />

            <Controller
              type='text'
              name='salePrice'
              control={control}
              render={({ value }) => (
                <TextInput
                  type='text'
                  className={`${base}__suffix`}
                  placeholder={t('insertSalePrice.placeholder')}
                  group='left'
                  onChange={(e) =>
                    handleChangePriceValue(e, 'salePrice', maxLengthPrice)
                  }
                  onBlur={(e) => handleBlurPriceValue(e, 'salePrice')}
                  value={value}
                  onKeyPress={validateKeyPress(new RegExp(/^\d*\.?\d*$/u))}
                  fullWidth
                  onPaste={(e) => handleDisableOnPaste(e)}
                  maxLength={maxLengthPrice}
                />
              )}
            />
          </InputGroup>
        </FormControl>
      </div>

      <FormControl label={t('maintenancePeriod.title')}>
        <InputGroup
          className={`${base}__maintenance-cycle`}
          error={errorMessageMaintenance}
          variants='ternary'
        >
          <Controller
            name='maintenancePeriod'
            control={control}
            render={(field) => (
              <TextInput
                type='text'
                group='right'
                className={`${base}__maintenance-period`}
                value={field.value}
                placeholder={t('insertNumber.placeholder')}
                isError={
                  Boolean(errors.maintenancePeriod?.message) ||
                  Boolean(errors.maintenanceUnit?.message)
                }
                withIcon={maintenancePeriodChecking && <Loader />}
                onChange={(e) => handleChangeValue(e, 'maintenancePeriod', 2)}
                onBlur={async () => {
                  setMaintenancePeriodChecking(true)
                  await trigger('maintenancePeriod')
                  setMaintenancePeriodChecking(false)
                }}
                onKeyPress={validateKeyPress(new RegExp(/[\p{N}]/u))}
                onPaste={(e) => handleDisableOnPaste(e)}
                disabled={maintenanceUnit === MAINTENANCE_UNIT.NONE}
                ref={field.ref}
              />
            )}
          />

          <Controller
            type='text'
            name='maintenanceUnit'
            control={control}
            render={({ value }) => (
              <Dropdown
                group='left'
                className={`${base}__maintenance-period`}
                placeholder={t('select.placeholder')}
                options={MAINTENANCE_UNIT_OPTIONS}
                selected={
                  findSelected(MAINTENANCE_UNIT_OPTIONS, value) || value
                }
                onChange={handleChangeMaintenanceUnit}
                onBlur={async () => {
                  setMaintenancePeriodChecking(true)
                  await trigger('maintenancePeriod')
                  setMaintenancePeriodChecking(false)
                }}
                isError={
                  Boolean(errors.maintenancePeriod?.message) ||
                  Boolean(errors.maintenanceUnit?.message)
                }
              />
            )}
          />
        </InputGroup>
      </FormControl>

      <FormControl label={t('remark.title')}>
        <Controller
          name='detail'
          control={control}
          render={(fields) => (
            <TextareaInput
              {...fields}
              hasCountable
              placeholder={t('insertProductDetail.placeholder')}
              onChange={(e) => {
                fields.onChange(trimFirstSpace(e))
              }}
            />
          )}
        />
      </FormControl>
    </>
  )
}

export default StockKeepingUnitDetail
